/**
 * PDF Prompt Ingest Helpers
 *
 * Shared utilities for creating Orbit TaskIngest events from PDF prompts.
 * Used by InlinePromptBox, PdfPromptRail, and PdfPromptOverlay.
 *
 * Ensures consistent provenance structure:
 * - identifier: "pdf:{docId}" - groups all prompts from a PDF into one collection
 * - title: fileName or "PDF Document"
 * - url: deep link back to the PDF viewer with taskID
 * - colorPaletteName: "cream" - warm cream aesthetic matching Andy's demo
 */

import {
  ColorPaletteName,
  EventType,
  TaskContentType,
  TaskID,
  TaskIngestEvent,
  TaskSpecType,
  generateUniqueID,
} from "@withorbit/core";

/**
 * Default color palette for PDF prompts.
 * Uses cream for a warm aesthetic matching Andy's demo.
 */
export const PDF_PROMPT_COLOR_PALETTE: ColorPaletteName = "cream";

/**
 * Prefix used for PDF collection identifiers.
 */
export const PDF_COLLECTION_PREFIX = "pdf:";

/**
 * Check if a collection ID refers to a PDF document.
 */
export function isPdfCollectionId(collectionId: string): boolean {
  return collectionId.startsWith(PDF_COLLECTION_PREFIX);
}

/**
 * Extract the docId from a PDF collection ID.
 * Returns null if the collection ID is not a PDF collection.
 */
export function getDocIdFromPdfCollectionId(collectionId: string): string | null {
  if (!isPdfCollectionId(collectionId)) return null;
  const docId = collectionId.slice(PDF_COLLECTION_PREFIX.length);
  return docId || null;
}

export interface PdfPromptIngestParams {
  /** Document ID (hash of PDF content) */
  docId: string;
  /** Original filename of the PDF */
  fileName?: string;
  /** The question/front of the prompt */
  question: string;
  /** The answer/back of the prompt */
  answer: string;
  /** Optional: specify task ID (otherwise generated) */
  taskId?: string;
  /** Optional: deep link URL back to the PDF viewer */
  deepLinkUrl?: string;
}

/**
 * Build a TaskIngestEvent for a PDF prompt.
 *
 * Creates a Memory QA task with provenance linking it to the PDF collection.
 */
export function buildPdfPromptTaskIngest(params: PdfPromptIngestParams): TaskIngestEvent {
  const { docId, fileName, question, answer, taskId, deepLinkUrl } = params;
  const entityID = (taskId ?? generateUniqueID()) as TaskID;

  return {
    type: EventType.TaskIngest,
    id: generateUniqueID(),
    entityID: entityID,
    timestampMillis: Date.now(),
    spec: {
      type: TaskSpecType.Memory,
      content: {
        type: TaskContentType.QA,
        body: { text: question.trim(), attachments: [] },
        answer: { text: answer.trim(), attachments: [] },
      },
    },
    provenance: {
      identifier: getPdfCollectionId(docId),
      title: fileName || "PDF Document",
      colorPaletteName: PDF_PROMPT_COLOR_PALETTE,
      ...(deepLinkUrl ? { url: deepLinkUrl } : {}),
    },
  };
}

/**
 * Build a deep link URL to open the PDF viewer at a specific prompt.
 *
 * @param docId - Document ID
 * @param taskId - Task/prompt ID to highlight
 * @returns Full URL path to the PDF viewer with taskID query param
 */
export function buildPdfPromptDeepLink(docId: string, taskId: string): string {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/pdf/viewer/${encodeURIComponent(docId)}?taskID=${encodeURIComponent(taskId)}`;
}

/**
 * Get the collection identifier for a PDF document.
 * Used to query tasks belonging to a specific PDF.
 */
export function getPdfCollectionId(docId: string): string {
  return `pdf:${docId}`;
}
